import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faXmark, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';

import './FabGroup.scss';

// `quickAction` ({ icon, label, onClick, loading }) adds a second round button next to the
// main FAB that runs one action directly, without opening the menu — desktop only (hidden
// on mobile by FabGroup.scss), where there's room for it beside the main button.
export default function FabGroup({ buttons = [], quickAction }) {
    const [open, setOpen] = useState(false);
    // A single action doesn't need a menu to expand — the FAB becomes a plain "+"
    // that triggers it directly, skipping the toggle/overlay/animation entirely.
    const singleButton = buttons.length === 1 ? buttons[0] : null;

    const toggle = () => setOpen(!open);
    const close = () => setOpen(false);

    const handleButtonClick = (btn) => {
        if (btn?.onClick) btn.onClick(); // exécute l’action
        close(); // ferme le menu
    };

    const handleMainClick = () => {
        if (singleButton) handleButtonClick(singleButton);
        else toggle();
    };

    return (
        <div className="fab-group">
            {/* Overlay */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fab-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={toggle} // ferme le menu si on clique à côté
                    />
                )}
            </AnimatePresence>

            {/* Boutons secondaires */}
            <AnimatePresence>
                {open && !singleButton &&
                    buttons.map((btn, index) => (
                        <motion.div
                            key={btn.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: -((index + 1) * 65) }}
                            exit={{ opacity: 0, y: 20 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                            className="fab-button-wrapper"
                        >
                            <button
                                className="btn-fab-group"
                                onClick={() => handleButtonClick(btn)}
                                form={btn?.form}
                            >
                                <FontAwesomeIcon icon={btn.icon} fixedWidth />
                                {btn.label}
                            </button>
                        </motion.div>
                    ))}
            </AnimatePresence>

            {/* Action rapide (desktop) */}
            {quickAction && (
                <motion.button
                    className="fab-main fab-quick"
                    onClick={() => { close(); quickAction.onClick?.(); }}
                    disabled={quickAction.loading}
                    whileTap={{ scale: 0.9 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={quickAction.label}
                    aria-label={quickAction.label}
                >
                    <FontAwesomeIcon icon={quickAction.loading ? faSpinner : quickAction.icon} spin={!!quickAction.loading} />
                </motion.button>
            )}

            {/* Bouton principal */}
            <motion.button
                className="fab-main"
                onClick={handleMainClick}
                whileTap={{ scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                data-tooltip-id="tooltip"
                data-tooltip-content={singleButton ? singleButton.label : "More Tools"}
            >
                <FontAwesomeIcon icon={singleButton ? faPlus : (open ? faXmark : faEllipsisVertical)} />
            </motion.button>
        </div>
    );
}
